import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useMappedState } from 'redux-react-hook';
import _isEmpty from 'lodash/isEmpty';
import { consultationSelector } from 'redux/selectors/consultation';
import {
  leaveReactConsultation,
  closeDuplicateSubmissionModal,
  internalRedirectSuccess,
} from 'redux/actions/consultation';
import {
  restartSession,
  evaluateSessionTimeout,
  closeSessionExpiryWarning,
  closeDemoNotice,
  extendSession,
  closeLeavePageWarning,
} from 'redux/actions/session';
import { usePrevious } from 'common/customHooks';
import { STRINGS } from 'config';
import { ColumnDiv } from 'styles/elements/div';
import { QuestionCard } from './card';
import { Endstate } from './endstate';
import { Resume } from './interstitials/resume';
import { LoginLoading } from './interstitials/loginLoading';
import { getModals } from './modal';
import { NoConsentGiven } from './answers/noConsentGiven';
import { TemplateInterstitial } from './interstitials/templateInterstitial';

export function Consultation({
  isClinicalReviewMode, treatmentCategoryCodeNameAsProps, isUserLoggedIn, product,
}) {
  const mapState = useCallback(state => consultationSelector(state), []);
  const {
    consultationInProgress,
    responseType,
    treatmentCategoryDetails,
    isSessionActive,
    consultationStartTime,
    isResumingConsultation,
    treatmentCategory,
    clinicalReviewConsultHeader,
    isDuplicateSubmission,
    isDemoNoticeOpen,
    isSessionExpiryWarningOpen,
    isConsultationEnded,
    isSessionExpiryTimeEmpty,
    isSessionExpired,
    isAttemptingToLeavePage,
    next,
    submissionDetails,
    isLoginQuestion,
    isInternalRedirectBlockResume,
    isContinueAsGuest,
    isLoginConsentRejected,
    interstitialText,
  } = useMappedState(mapState);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(evaluateSessionTimeout());
  }, [dispatch]);

  useEffect(() => {
    if (!isSessionActive) {
      dispatch(restartSession());
    }
  }, [dispatch, isSessionActive]);

  // The order here matters. isResumingConsultation is determined by consultationStartTime.
  // That can only be set AFTER the initial render's value has been memoized.
  const [isResumeConsultation, setIsResumeConsultation] = useState(isResumingConsultation);
  const [isLoggingIn] = useState(isLoginQuestion);
  const [isInternalRedirect] = useState(isInternalRedirectBlockResume);

  const previousConsultationStartTime = usePrevious(consultationStartTime);
  useEffect(() => {
    if (previousConsultationStartTime && !consultationStartTime) {
      setIsResumeConsultation(false);
    }
  }, [previousConsultationStartTime, consultationStartTime]);

  useEffect(() => {
    if (isResumeConsultation && isConsultationEnded) {
      dispatch(restartSession());
    }
  }, [dispatch, isResumeConsultation, isConsultationEnded]);

  useEffect(() => {
    if (isInternalRedirect) {
      dispatch(internalRedirectSuccess());
    }
  }, [dispatch, isInternalRedirect]);

  const { SessionExpiryWarningModal, DemoNoticeModal } = useMemo(() => getModals(product), [product]);

  if (!responseType || ((responseType !== STRINGS.RESPONSE_TYPE.ERROR) && treatmentCategoryCodeNameAsProps
    && (treatmentCategoryCodeNameAsProps !== treatmentCategory)) || (isResumeConsultation && isConsultationEnded)) {
    return (<div />);
  }
  if (isLoginConsentRejected && !isContinueAsGuest) {
    return (<NoConsentGiven />);
  }
  if (isResumeConsultation && !isSessionExpiryTimeEmpty && !isSessionExpired && !(isLoggingIn && isUserLoggedIn) && !isLoginConsentRejected) {
    return (<Resume consultationStartTime={consultationStartTime} condition={treatmentCategoryDetails.name} />);
  }
  if (isResumeConsultation && !isSessionExpiryTimeEmpty && !isSessionExpired && isLoggingIn && isUserLoggedIn && !isLoginConsentRejected) {
    return (<LoginLoading />);
  }
  if (interstitialText && consultationInProgress) {
    return (<TemplateInterstitial isClinicalReviewMode={isClinicalReviewMode} header={clinicalReviewConsultHeader} product={product} />);
  }

  const consultationEndedDispatch = product === STRINGS.PRODUCT.E_SPECIALIST
    ? () => {
      dispatch(restartSession());
    }
    : () => dispatch(leaveReactConsultation());

  return (
    <ColumnDiv modifier={['fillHeight', 'flexFill']}>
      {consultationInProgress ? (
        <QuestionCard isClinicalReviewMode={isClinicalReviewMode} header={clinicalReviewConsultHeader} product={product} />
      ) : (
        <Endstate
          isDuplicateSubmissionModalOpen={isDuplicateSubmission || (isAttemptingToLeavePage && !_isEmpty(submissionDetails))}
          onConfirmDuplicateSubmissionModal={() => dispatch(leaveReactConsultation())}
          isConsultationEndedModalOpen={isAttemptingToLeavePage && !next && _isEmpty(submissionDetails)}
          onConfirmConsultationEndedModal={consultationEndedDispatch}
          onCloseConsultationEndedModal={() => dispatch(closeLeavePageWarning())}
          onCloseDuplicateSubmissionModal={() => dispatch(closeDuplicateSubmissionModal())}
        />
      )}
      {process.env.REACT_APP_ENVIRONMENT === 'demo' && (
        <DemoNoticeModal
          isOpen={isDemoNoticeOpen && !isAttemptingToLeavePage && !isSessionExpiryWarningOpen}
          onSelectConfirm={() => dispatch(closeDemoNotice())}
        />
      )}
      <SessionExpiryWarningModal
        isOpen={isSessionExpiryWarningOpen && !isAttemptingToLeavePage}
        onSelectCancel={consultationEndedDispatch}
        onSelectConfirm={() => dispatch(extendSession()).then(() => dispatch(closeSessionExpiryWarning()))}
      />
    </ColumnDiv>
  );
}
